import { useState } from "react";
import { PencilSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { ProductSelect } from "./ProductSelect";

const withKey = (it) => ({ _key: crypto.randomUUID(), product_name: it.product_name, quantity: it.quantity });

export function EditOrderDialog({ order, products, isAdmin, onClose, onSaved }) {
  const [customerName, setCustomerName] = useState(order.customer_name);
  const [location, setLocation] = useState(order.location);
  const [notes, setNotes] = useState(order.notes || "");
  const [items, setItems] = useState(order.items.map(withKey));
  const [saving, setSaving] = useState(false);

  const setItem = (i, key, value) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [key]: value } : it)));

  const save = async () => {
    const parsed = items.map((it) => ({ product_name: String(it.product_name).trim(), quantity: parseFloat(it.quantity) }));
    if (!customerName.trim() || !location.trim()) {
      toast.error("Customer name and location are required");
      return;
    }
    if (!parsed.length || parsed.some((it) => !it.product_name || !(it.quantity > 0))) {
      toast.error("Every line item needs a product and quantity > 0");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/orders/${order.id}`, {
        customer_name: customerName.trim(),
        location: location.trim(),
        notes: notes.trim() || null,
        items: parsed,
      });
      toast.success(`Order ${data.order_number} updated`);
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="edit-order-dialog">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
            <PencilSimple size={18} weight="duotone" /> Edit Order {order.order_number}
          </h2>
          <button data-testid="edit-order-close-button" onClick={onClose}
            className="text-slate-400 transition-colors duration-150 hover:text-slate-900">
            <X size={18} />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer Name</label>
              <input data-testid="edit-customer-name-input" value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Location</label>
              <input data-testid="edit-location-input" value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Line Items</label>
            <div className="mt-2 space-y-2">
              {items.map((it, i) => (
                <div key={it._key} className="flex items-center gap-2">
                  <div className="min-w-0 flex-1 [&>button]:mt-0">
                    <ProductSelect products={products} value={it.product_name} testId={`edit-item-product-${i}`}
                      allowCustom={isAdmin} placeholder="Product"
                      onChange={(v) => setItem(i, "product_name", v)} />
                  </div>
                  <input data-testid={`edit-item-qty-${i}`} type="number" min="0" step="any" value={it.quantity}
                    onChange={(e) => setItem(i, "quantity", e.target.value)} placeholder="Qty"
                    className="w-20 rounded-md border border-slate-300 px-2 py-2 text-right font-mono text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
                  {items.length > 1 && (
                    <button data-testid={`edit-item-remove-${i}`}
                      onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))}
                      className="text-slate-400 transition-colors duration-150 hover:text-red-600">
                      <X size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" data-testid="edit-add-item-button"
              onClick={() => setItems((p) => [...p, withKey({ product_name: "", quantity: "" })])}
              className="mt-2 text-sm font-medium text-slate-600 underline-offset-4 hover:underline">
              + Add line item
            </button>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notes</label>
            <textarea data-testid="edit-notes-input" value={notes} rows={2}
              onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes…"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
            <button data-testid="edit-order-cancel-button" onClick={onClose}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100">
              Cancel
            </button>
            <button data-testid="edit-order-save-button" onClick={save} disabled={saving}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-slate-700 disabled:opacity-60">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
